import { z } from 'zod';

/**
 * Messages de signalisation WebRTC échangés via Broadcast sur le canal
 * Realtime privé de la salle (`visio-room:{roomId}`). Validés côté client —
 * la sécurité réelle vient de l'autorisation du canal (migration
 * 0236_visio_realtime_authorization.sql), ce schéma protège juste contre un
 * payload malformé/inattendu.
 */

const sessionDescriptionSchema = z.object({
  type: z.enum(['offer', 'answer', 'pranswer', 'rollback']),
  sdp: z.string().optional(),
});

const iceCandidateSchema = z.object({
  candidate: z.string().optional(),
  sdpMid: z.string().nullable().optional(),
  sdpMLineIndex: z.number().nullable().optional(),
  usernameFragment: z.string().nullable().optional(),
});

export const signalingMessageSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('offer'),
    from: z.string(),
    to: z.string(),
    data: sessionDescriptionSchema,
  }),
  z.object({
    kind: z.literal('answer'),
    from: z.string(),
    to: z.string(),
    data: sessionDescriptionSchema,
  }),
  z.object({
    kind: z.literal('ice-candidate'),
    from: z.string(),
    to: z.string(),
    data: iceCandidateSchema,
  }),
  // LEGACY (avant la salle à plusieurs) : l'hôte l'envoyait à chaque pair pour dire « fin de
  // la réunion ». Les nouveaux clients envoient `room-ended` (+ ce `bye`, tant que d'anciens
  // clients tournent). Reçu, il signifie qu'UN pair part — jamais « la salle est terminée »
  // à lui seul (cf. `WebRtcVideoService.handleSignal`).
  z.object({ kind: z.literal('bye'), from: z.string(), to: z.string() }),
  // La réunion est TERMINÉE par l'hôte (`end_visio_room`) : diffusé à tous, sans destinataire.
  // Un membre pouvant forger ce message, le récepteur le VÉRIFIE côté serveur
  // (`video_rooms.ended_at`) avant d'afficher « séance terminée ».
  z.object({ kind: z.literal('room-ended'), from: z.string() }),
  // L'hôte demande à un participant de COUPER son micro (jamais de le rouvrir).
  z.object({ kind: z.literal('mute-request'), from: z.string(), to: z.string() }),
  // L'hôte EXCLUT un participant : celui-ci doit quitter immédiatement (≠ `remove_visio_participant`,
  // l'écriture DB — ce signal fait sortir la connexion WebRTC déjà établie SANS attendre que
  // l'exclu se reconnecte pour découvrir `removed_from_room`).
  z.object({ kind: z.literal('excluded'), from: z.string(), to: z.string() }),
  z.object({
    kind: z.literal('transcription-state'),
    from: z.string(),
    to: z.string(),
    data: z.object({ active: z.boolean() }),
  }),
  z.object({
    kind: z.literal('screen-share-state'),
    from: z.string(),
    to: z.string(),
    data: z.object({ sharing: z.boolean() }),
  }),
]);

export type SignalingMessage = z.infer<typeof signalingMessageSchema>;

export function parseSignalingMessage(raw: unknown): SignalingMessage | null {
  const result = signalingMessageSchema.safeParse(raw);
  return result.success ? result.data : null;
}
